'use client';

import { useWorkingPlatform } from '@repo/platform';
import { MainTitle } from '@repo/ui';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ContentCard } from '@/app/_components/chat-content/ContentCard';
import { ContentCategory } from '@/app/_components/chat-content/ContentCategory';
import { contentPageConverter } from '@/app/home/_components/content-page-converter';
import { getContents } from '@/lib/api/contents';
import { CONTENT_CARD_PAGE_PER_COUNT } from '@/lib/constants/content-section';
import { ADS_HIDDEN_PARTNERS, CONTENT_CATEGORY_HIDDEN_PARTNERS, CONTENTS_FILTER_PARTNERS } from '@/lib/constants/platform';
import { contentsAtom } from '@/lib/stores/home';
import type { ContentCardItem, QuestionContent } from '@/lib/types/content';

export const ContentCardSection = ({ initialContents }: { initialContents: QuestionContent[] }) => {
  const { workingPlatform } = useWorkingPlatform();
  const isHideAds = ADS_HIDDEN_PARTNERS.includes(workingPlatform);

  return <ContentCards isHideAds={isHideAds} initialContents={initialContents} />;
};

const ContentCards = ({ isHideAds, initialContents }: { isHideAds?: boolean; initialContents: QuestionContent[] }) => {
  const { workingPlatform } = useWorkingPlatform();
  const [contents, setContents] = useAtom(contentsAtom);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [hasSecondDepth, setHasSecondDepth] = useState(false);
  const isLoading = useRef(false);
  const isLastPage = useRef(false);
  const pageBottomObserver = useRef<HTMLDivElement>(null);
  const contentCardsContainerRef = useRef<HTMLDivElement>(null);
  const prevCategoryFilterRef = useRef<string | undefined>(undefined);
  const categoryFilterChangedRef = useRef(false);

  // 초기 마운트 시 서버에서 받은 컨텐츠 설정
  const firstPageContent = useMemo(() => contentPageConverter(1, initialContents, 0, isHideAds), [initialContents, isHideAds]);

  const fetchContents = useCallback(
    async (pageNo: number, filter?: string, startIndex: number = 0) => {
      const response = await getContents(
        pageNo,
        CONTENTS_FILTER_PARTNERS.includes(workingPlatform) ? workingPlatform : undefined,
        CONTENT_CATEGORY_HIDDEN_PARTNERS.includes(workingPlatform) ? undefined : filter
      );

      const newContents: QuestionContent[] = await response.json();
      const newCardItems: ContentCardItem[] = contentPageConverter(pageNo, newContents, startIndex, isHideAds);

      isLastPage.current = newContents.length < CONTENT_CARD_PAGE_PER_COUNT;
      return newCardItems;
    },
    [isHideAds, workingPlatform]
  );

  const getMoreContents = useCallback(
    async (pageNo: number, filter?: string) => {
      if (isLoading.current) return;
      isLoading.current = true;

      try {
        const newCardItems = await fetchContents(pageNo, filter, contents.cardItems.length);
        setContents({ currentPage: pageNo, cardItems: contents.cardItems.concat(newCardItems) });
      } finally {
        isLoading.current = false;
      }
    },
    [fetchContents, contents.cardItems, setContents]
  );

  const loadContents = useCallback(
    async (filter?: string) => {
      isLoading.current = true;
      isLastPage.current = false;

      try {
        const newCardItems = await fetchContents(1, filter, 0);
        setContents({ currentPage: 1, cardItems: newCardItems });
      } catch {
        setContents({ currentPage: 1, cardItems: [] });
      } finally {
        isLoading.current = false;
      }
    },
    [fetchContents, setContents]
  );

  const onIntersect = useCallback(
    async ([entry]: IntersectionObserverEntry[]) => {
      if (!entry || isLastPage.current) return;

      if (entry.isIntersecting && !isLoading.current) {
        getMoreContents((contents.currentPage || 1) + 1, categoryFilter);
      }
    },
    [getMoreContents, contents.currentPage, categoryFilter]
  );

  useEffect(() => {
    if (contents.currentPage === 1 && contents.cardItems.length === 0 && firstPageContent.length > 0) {
      setContents({ currentPage: 1, cardItems: firstPageContent });
    }
  }, [contents.currentPage, contents.cardItems.length, firstPageContent, setContents]);

  // 카테고리 필터 변경 감지 및 콘텐츠 재로드
  useEffect(() => {
    // 초기 마운트 시 undefined면 재조회하지 않음
    if (categoryFilter === undefined && prevCategoryFilterRef.current === undefined) {
      prevCategoryFilterRef.current = categoryFilter;
      return;
    }

    // 카테고리가 변경된 경우에만 재로드
    if (prevCategoryFilterRef.current !== categoryFilter) {
      prevCategoryFilterRef.current = categoryFilter;
      categoryFilterChangedRef.current = true;
      loadContents(categoryFilter);
    }
  }, [categoryFilter]);

  // 카테고리 변경 후 콘텐츠가 로드되면 첫 번째 카드로 스크롤
  useEffect(() => {
    if (categoryFilterChangedRef.current && contents.cardItems.length > 0 && contentCardsContainerRef.current) {
      categoryFilterChangedRef.current = false;
      requestAnimationFrame(() => {
        contentCardsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [contents.cardItems.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersect, { rootMargin: '700px' });
    if (pageBottomObserver.current) observer.observe(pageBottomObserver.current);
    return () => observer.disconnect();
  }, [onIntersect]);

  return (
    <div className={'relative w-full compact:max-w-[390px] max-w-full'}>
      <MainTitle size={'small'} title={'전문가들이 꼼꼼히 검증하여,\n엄선된 지식 콘텐츠를 만나보세요'} />

      {!CONTENT_CATEGORY_HIDDEN_PARTNERS.includes(workingPlatform) && (
        <ContentCategory onCategoryFilterChange={setCategoryFilter} onHasSecondDepthChange={setHasSecondDepth} />
      )}

      <div
        ref={contentCardsContainerRef}
        className={'grid compact:grid-cols-1 grid-cols-2 gap-normal-medium'}
        style={{ scrollMarginTop: hasSecondDepth ? '180px' : '124px' }}
      >
        {(contents.cardItems.length > 0 ? contents.cardItems : firstPageContent).map((item, index) => (
          <ContentCard item={item} key={item.id ? `${item.id}` : `advertisement-${index}`} isShowAdvertisementIcon={item.type === 'advertisement'} />
        ))}
      </div>
      <div ref={pageBottomObserver} className={'absolute bottom-0 w-full h-[120px]'} />
    </div>
  );
};
